/**
 * Supported media types
 */
export enum MediaType {
  Metrics = "application/vnd.skore.metrics.v1",
  Comparison = "application/vnd.skore.comparison.v1+json",
  Vega = "application/vnd.vega.v5+json",
  Plotly = "application/vnd.plotly.v1+json",
  TableReport = "application/vnd.skrub.table-report.v1+json",
  DataFrame = "application/vnd.dataframe",
}

/**
 * Report URN prefixes
 */
export enum ReportUrnPrefix {
  Estimator = "skore:report:estimator",
  CrossValidation = "skore:report:cross-validation",
  Comparison = "skore:report:comparison",
}

export function extractIdFromUrn(urn: string) {
  return parseInt(urn.split(":").pop() ?? "-1");
}
/**
 * A workspace member
 */
export interface WorkspaceMemberDto {
  workspace_id: number;
  user_id: string;
  role: "owner" | "member";
  invited_by?: string;
}

/**
 * A workspace
 */
export interface WorkspaceDto {
  id: number;
  owner_id: string;
  public_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  members?: WorkspaceMemberDto[];
}

/**
 * An invitation to a workspace
 */
export interface InvitationDto {
  email: string;
  role: "owner" | "member";
  permissions: string[];
}

/**
 * A user
 */
export interface UserDto {
  id: string;
  email_verified: boolean;
  workspaces: { [id: string]: string };
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
}

export function getUserFriendlyName(user: UserDto) {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.email;
}

/**
 * A project
 */
export interface ProjectDto {
  id: number;
  public_id: string;
  name: string;
  workspace_id: number;
  creator_id: string;
  estimator_report_count: number;
  cross_validation_report_count: number;
  presentation_count: number;
  comparison_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * A project item is a single item in the project.
 *
 * It's contributed by a user python side.
 */
export interface ProjectItemDto {
  name: string;
  media_type: string;
  value: any;
  updated_at: string;
  created_at: string;
  note: string | null;
  version: number;
}

export enum ReportType {
  EstimatorReport,
  CrossValidationReport,
}

/**
 * An experiment group.
 */
export interface ReportGroupDto {
  dataset: string;
  reports: {
    ml_task: string;
    estimator_class_name: string;
  }[];
}

export interface MetricDto {
  name: string;
  verbose_name: string;
  data_source: string | null;
  value: number;
  greater_is_better: boolean | null;
  position: number | null;
}

export interface EstimatorReportSummaryDto {
  id: number;
  urn: string;
  project_id: number;
  creator_id: string;
  key: string;
  ml_task: string;
  estimator_class_name: string;
  metrics: MetricDto[];
  dataset_fingerprint: string;
  updated_at: string;
  created_at: string;
}

export interface CrossValidationReportSummaryDto extends EstimatorReportSummaryDto {
  splitting_strategy_name: string;
}

/**
 * ReportMedia
 */
export interface ReportMediaDto {
  checksum: string;
  data_source: string | null;
  size_bytes: number;
  name: string;
  verbose_name: string;
  category: string;
  method: string | null;
  content_type: string;
  presigned_url: string;
}

/**
 * An EstimatorReport DTO.
 */
export interface EstimatorReportDto extends EstimatorReportSummaryDto {
  medias: ReportMediaDto[];
  parents_urns: string[];
}

/**
 * A cross validation split DTO.
 */
export interface CrossValidationSplitDto {
  /**
   * The train subset information.
   */
  train: {
    sample_count: number;
    target_distribution: number[] | null;
    groups: number[] | null;
  };
  test: {
    sample_count: number;
    target_distribution: number[] | null;
    groups: number[] | null;
  };
  train_test_distribution: number[];
}

/**
 * A cross validation report DTO.
 */
export interface CrossValidationReportDto extends CrossValidationReportSummaryDto {
  medias: ReportMediaDto[];
  dataset_size: number;
  groups: number[] | null;
  class_names: string[] | null;
  target_range: [number, number] | null;
  estimators: number[];
  splitting_strategy: {
    strategy_name: string;
    repeat_count: number;
    seed: string | null;
    splits: CrossValidationSplitDto[];
  };
}

/**
 * Get report's name from a report dto
 * @param report a report dto
 * @returns the report's name
 */
export function getReportName(report: EstimatorReportDto | CrossValidationReportDto) {
  return report.key ? report.key : `xp #${report.id}`;
}

/**
 * Check if a report is a cross validation report
 * @param report a report dto
 * @returns true if the report is a cross validation report, false otherwise
 */
export function isCrossValidationReport(
  report: EstimatorReportSummaryDto | CrossValidationReportSummaryDto
) {
  return (report as CrossValidationReportSummaryDto).splitting_strategy_name !== undefined;
}

/**
 * Report comparison DTO
 *
 * The result of a set of reports comparison.
 */
export interface EstimatorReportComparisonDto {
  id: number;
  project_id: number;
  creator_id: string;
  name: string;
  compared_reports: number[];
  best_trade_offs: number[];
  best_trade_offs_explanations: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface ApiLivenessDto {
  version: string;
  hash_commit: string | null;
}

export interface ApiHealthDto extends ApiLivenessDto {
  redis: boolean;
  redis_response_time_ms: number;
  database: boolean;
  database_response_time_ms: number;
}

export interface UserAPIKeyDto {
  id: number;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}
